import { AxiosError } from "axios";
import { getServiceFetcher } from "../../api/ApiConfig";
import { asyncDebounce } from "../../utils";
import { FieldValidationParams } from "../types";
import { fieldValidationCache } from "./fieldValidationCache";
import { validationException } from "./validator.utils";
import isUndefined from "lodash/isUndefined";

export const validateTaxCodeFn = async (
  values: Record<string, unknown>,
): Promise<{
  valid: boolean;
}> => {
  const req = await getServiceFetcher().post("/taxcode/validate", values);
  return req.data;
};

const debouncedValidatorFn = asyncDebounce(validateTaxCodeFn, 800);

const CONTROL_NAME = "taxCode";
const INVALID_CONTROL_MESSAGE = "Invalid tax code.";

const resolveIfNotActive = (
  active: boolean,
  resolver: (value: boolean) => void,
) => {
  if (!active) {
    resolver(true);
  }
};
const checkIsInvalidError = (cb: () => void, error = 0) => {
  if (error >= 400 && error < 500) {
    cb();
  }
};

export const validateTaxCodeField = async ({
  args,
  names,
  onValidated,
  onInvalid,
  onValidationStarted,
}: FieldValidationParams) => {
  return new Promise((resolve, reject) => {
    const [active, value, defs, schema] = args;
    const { parentData: data = {} } = schema;

    resolveIfNotActive(active, resolve);
    const { instancePath = "" } = schema;
    const paths = instancePath.split("/");
    const name = paths[paths.length - 1];
    const { firstNameField = "firstName", lastNameField = "lastName" } = defs;
    const checkValidated = () => {
      if (onValidated) {
        onValidated(names, name, CONTROL_NAME, [firstNameField, lastNameField]);
      }
    };
    const checkInvalid = (errorCode = 0) => {
      if (onInvalid) {
        onInvalid(names, name, CONTROL_NAME, errorCode, [
          firstNameField,
          lastNameField,
        ]);
      }
    };

    if (value.length !== 16) {
      checkInvalid();
      return reject(
        validationException(
          "minLength",
          "At least 18 alphanumeric characters are required.",
        ),
      );
    }
    const firstNameValue = data[firstNameField as string];
    const lastNameValue = data[lastNameField as string];
    if ([firstNameValue, lastNameValue].some(isUndefined)) {
      checkInvalid();
      return reject(validationException("taxCodeMissingData", "Missing data"));
    }

    const cacheDeps = [firstNameValue, lastNameValue];
    const cacheName = `${name}${value}${firstNameValue}${lastNameValue}`;
    const values = {
      firstName: firstNameValue,
      lastName: lastNameValue,
      taxCode: value,
    };

    // if the validation failed previously and was cached as invalid just reject.
    const isCachedAsInvalid =
      fieldValidationCache.isFieldInvalidValid(cacheName);
    if (isCachedAsInvalid) {
      checkInvalid(0);
      return reject(validationException(CONTROL_NAME, INVALID_CONTROL_MESSAGE));
    }

    const shouldRevalidate = fieldValidationCache.shouldRevalidate(
      cacheName,
      value,
      cacheDeps,
    );

    if (!shouldRevalidate) {
      const cachedValue = fieldValidationCache.fields.get(cacheName)?.result;
      if (cachedValue) {
        checkValidated();
        return resolve(true);
      }
      if (cachedValue === false) {
        checkInvalid(0);
        return reject(
          validationException(CONTROL_NAME, INVALID_CONTROL_MESSAGE),
        );
      }
    }
    if (onValidationStarted) {
      onValidationStarted(CONTROL_NAME, name);
    }
    debouncedValidatorFn(values)
      .then(req => {
        if (!req.valid) {
          checkInvalid(0);
          fieldValidationCache.addInvalidItem(cacheName, {
            deps: cacheDeps,
            value,
          });
          return reject(
            validationException(CONTROL_NAME, INVALID_CONTROL_MESSAGE),
          );
        }
        checkValidated();
        fieldValidationCache.add(cacheName, {
          deps: cacheDeps,
          value,
          result: true,
        });
        return resolve(true);
      })
      .catch(e => {
        const error = e as AxiosError;
        let status = error.response?.status;
        checkIsInvalidError(() => {
          fieldValidationCache.addInvalidItem(cacheName, {
            deps: cacheDeps,
            value,
          });
        }, error.response?.status);
        status ||= 400;
        checkInvalid(status);
        return reject(
          validationException(CONTROL_NAME, INVALID_CONTROL_MESSAGE),
        );
      });
  });
};
